import { createFileRoute, LinkProps } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@/components/core/Button';
import {
  IconApplications,
  IconBackpack,
  IconBus,
  IconPayers,
  IconPeriods,
  IconSearch,
  IconTicketPlus,
  IconUserPlus,
  IconYouthCard,
} from '@/icons';
import { OrganizationCreditInfo } from '@/components/OrganizationCreditInfo';
import { SysadminMissingPeriodInfo } from '@/components/Organizations/SysadminMissingPeriodInfo';
import { ProfileCreateForm } from '@/components/Travellers/ProfileCreateForm';
import { DialogWindow } from '@/components/Dialog';
import { useMemo, useState } from 'react';
import { useTenantID } from '@/hooks/useTenantID';
import { CenteredColumn } from '@/components/CenteredColumn';
import { Section } from '@/components/Section';
import { useAuth } from '@/stores/auth';
import { DocumentHead } from '@/components/core/DocumentHead';
import { FullsizePage } from '@/components/FullsizePage';
import {
  useGetCurrentOrganization,
  useListOrganizationApplications,
} from '@/api/trafikportalen';
import { useIncompletePayersCount } from '@/hooks/useIncompletePayersCount';
import { useTutorial } from '@/hooks/useTutorial';
import { useBranding } from '@/hooks/useBranding';
import { NoClaimWarning } from '@/components/NoClaimWarning';
import { DashboardStudentClaimErrors } from '@/components/Students/DashboardStudentClaimErrors';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useCanBuyTickets } from '@/hooks/useCanBuyTickets';
import { useStudentCategoryWarnings } from '@/hooks/useStudentCategoryWarnings';
import { useHasSchoolDepartmentAccess } from '@/hooks/useHasSchoolDepartmentAccess';
import { useHasNonSchoolDepartmentAccess } from '@/hooks/useHasNonSchoolDepartmentAccess';
import { useAccessibleStudentCategories } from '@/hooks/useAccessibleStudentCategories';
import {
  COLLECTION_TYPE_LABELS,
  collectionTypeToStudentType,
  STUDENT_TYPE_VIEW_ROUTE,
  StudentCollectionType,
} from '@/helpers/collectionTypes';

const STUDENT_CATEGORY_IMPORT_ROUTE: Record<
  StudentCollectionType,
  LinkProps['to']
> = {
  'school-student-travel': '/$tenantId/students/travels/import',
  'school-transportation': '/$tenantId/students/transportation/import',
  'school-child-and-youth': '/$tenantId/students/child-and-youth/import',
};

const STUDENT_CATEGORY_ICON: Record<StudentCollectionType, React.ReactNode> = {
  'school-student-travel': <IconBus />,
  'school-transportation': <IconBackpack />,
  'school-child-and-youth': <IconYouthCard />,
};

export const Route = createFileRoute('/$tenantId/_layout/')({
  component: Index,
});

function Index() {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const { userProfile } = useAuth();
  const branding = useBranding();
  useTutorial(); // Initialize tutorial hook

  const [newTravellerOpen, setNewTravellerOpen] = useState(false);

  const isSysadmin = !userProfile?.organization_id;

  const hasSchoolDepartmentAccess = useHasSchoolDepartmentAccess();
  const hasNonSchoolDepartmentAccess = useHasNonSchoolDepartmentAccess();
  const accessibleStudentCategories = useAccessibleStudentCategories({
    requireWriteAccess: true,
  });
  const studentsDisabled = useStudentsDisabled();
  const dashboardStudentCategories = studentsDisabled
    ? []
    : accessibleStudentCategories;
  const hasTravellersToBuyFor =
    hasNonSchoolDepartmentAccess === true ||
    dashboardStudentCategories.length > 0;
  const canBuyTickets = useCanBuyTickets(!isSysadmin && hasTravellersToBuyFor);
  const showBuyTicket = hasTravellersToBuyFor && canBuyTickets !== false;
  const showNewTraveller = hasNonSchoolDepartmentAccess === true;
  const hasDashboardActions =
    showBuyTicket || showNewTraveller || dashboardStudentCategories.length > 0;

  const { data: organizationApplications } = useListOrganizationApplications({
    query: { enabled: isSysadmin },
  });

  const incompletePayersCount = useIncompletePayersCount(isSysadmin);

  const submittedApplications = useMemo(() => {
    if (!organizationApplications) return 0;

    return organizationApplications.filter(
      (application) => application.status === 'submitted',
    ).length;
  }, [organizationApplications]);

  return (
    <FullsizePage>
      <DocumentHead
        titleKey="page.dashboard.title"
        descriptionKey="page.dashboard.description"
      />
      <CenteredColumn className="mt-10 gap-10" data-tutorial="dashboard">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-bold">
            <FormattedMessage
              defaultMessage="Välkommen till {serviceName}"
              values={{ serviceName: branding?.serviceName }}
            />
          </h1>
          <p className="text-xl">{branding?.tagLine}</p>
        </div>
        {isSysadmin ? (
          <>
            {!studentsDisabled && <SysadminMissingPeriodInfo />}
            <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
              <BigButton
                icon={<IconApplications />}
                text={formatMessage({ defaultMessage: 'Ansökningar' })}
                description={formatMessage(
                  {
                    defaultMessage:
                      '{count, plural, one {# ohanterad} other {# ohanterade}}',
                  },
                  { count: submittedApplications },
                )}
                to="/$tenantId/organizations/$view"
                params={{ tenantId, view: 'applications' }}
                search={{ filter: 'submitted' }}
              />
              <BigButton
                icon={<IconPayers />}
                text={formatMessage({ defaultMessage: 'Fakturamottagare' })}
                description={formatMessage(
                  {
                    defaultMessage:
                      '{count, plural, one {# ohanterad} other {# ohanterade}}',
                  },
                  { count: incompletePayersCount },
                )}
                to="/$tenantId/payers"
                params={{ tenantId }}
                search={{ filter: 'incomplete' }}
              />
              <BigButton
                text={formatMessage({ defaultMessage: 'Sök resenärer' })}
                description={formatMessage({
                  defaultMessage: 'Sök resenärer',
                })}
                icon={<IconSearch />}
                to="/$tenantId/search"
                params={{ tenantId }}
                tutorialId="dashboard-search"
              />
            </div>
          </>
        ) : (
          <>
            {!studentsDisabled && hasSchoolDepartmentAccess && (
              <DashboardClaimWarnings />
            )}
            {!studentsDisabled && hasSchoolDepartmentAccess && (
              <DashboardStudentClaimErrors />
            )}
            {hasNonSchoolDepartmentAccess && (
              <Section tutorialId="credit-info">
                <OrganizationCreditInfo />
              </Section>
            )}
            {hasDashboardActions && (
              <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
                {showBuyTicket && (
                  <BigButton
                    text={formatMessage({ defaultMessage: 'Köp biljett' })}
                    icon={<IconTicketPlus />}
                    to="/$tenantId/buy-ticket"
                    params={{ tenantId }}
                    tutorialId="buy-ticket"
                  />
                )}
                {showNewTraveller && (
                  <BigButton
                    text={formatMessage({ defaultMessage: 'Ny resenär' })}
                    icon={<IconUserPlus />}
                    onClick={() => setNewTravellerOpen(true)}
                    tutorialId="new-traveller"
                  />
                )}
                {dashboardStudentCategories.map((collectionType) => (
                  <BigButton
                    key={collectionType}
                    text={`${formatMessage(
                      COLLECTION_TYPE_LABELS[collectionType],
                    )}: ${formatMessage({
                      id: 'newImport',
                    })}`}
                    icon={STUDENT_CATEGORY_ICON[collectionType]}
                    to={STUDENT_CATEGORY_IMPORT_ROUTE[collectionType]}
                    params={{ tenantId }}
                    tutorialId={`import-${collectionType}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CenteredColumn>
      {showNewTraveller && (
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Lägg till resenär' })}
          content={<ProfileCreateForm onOpenChange={setNewTravellerOpen} />}
          onOpenChange={setNewTravellerOpen}
          open={newTravellerOpen}
        />
      )}
    </FullsizePage>
  );
}

const DashboardClaimWarnings = () => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const warnings = useStudentCategoryWarnings();
  const { data: organization } = useGetCurrentOrganization();
  const orgManagesPeriods = organization?.selfmanaged_school_claims === true;

  if (warnings.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-2">
      {warnings.map((w) => (
        <NoClaimWarning
          key={w.collectionType}
          categoryName={formatMessage(w.label)}
          canCreate={orgManagesPeriods}
          action={
            <Button
              color="yellow"
              variant="filled"
              startIcon={<IconPeriods />}
              to={
                STUDENT_TYPE_VIEW_ROUTE[
                  collectionTypeToStudentType(
                    w.collectionType as StudentCollectionType,
                  )
                ]
              }
              params={{ tenantId, view: 'periods' }}
            >
              {orgManagesPeriods ? (
                <FormattedMessage defaultMessage="Hantera perioder" />
              ) : (
                <FormattedMessage defaultMessage="Visa perioder" />
              )}
            </Button>
          }
        />
      ))}
    </div>
  );
};

const BigButton = ({
  text,
  icon,
  onClick,
  to,
  params,
  description,
  search,
  tutorialId,
}: {
  text: string;
  icon: React.ReactNode;
  description?: string;
  onClick?: () => void;
  to?: LinkProps['to'];
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  tutorialId?: string;
}) => {
  return (
    <Button
      className="h-full w-auto max-w-full flex-1 gap-5 p-10 hover:bg-primary-500 hover:text-contrast-primary"
      onClick={onClick}
      to={to}
      params={params}
      search={search}
      data-tutorial={tutorialId}
    >
      <div className="[&>svg]:size-10">{icon}</div>
      <div className="flex flex-col gap-1">
        <p className="text-lg font-bold">{text}</p>
        {description && <p className="opacity-70">{description}</p>}
      </div>
    </Button>
  );
};
